/**
 * lobbyView.ts - Lobby building view (same pattern as buildingView.ts)
 *
 * Renders:
 * 1. Lobby building floors with facilities (vertical stack, bottom-up)
 *    - Each floor: facility background image + NPC ghost + props overlay
 *    - Empty floor: dashed border + "Build Facility" text
 *    - Floor click -> 4-tab detail popup (Info / Objects / Upgrade / Demolish)
 * 2. Waiting queue with patience bars (bottom)
 * 3. "Add Floor" button at top
 */

import type { GameStateData, LobbyFloor } from '../game/state.js';
import { getEmptyRoomImage } from '../game/state.js';
import { GHOST_DEFS } from '../game/data/ghosts.js';
import { LOBBY_FACILITY_DEFS, LOBBY_FACILITY_BUILD_ORDER, lobbyFacilityUpgradeCost } from '../game/data/lobbyFacilities.js';
import type { LobbyFacilityType } from '../game/data/lobbyFacilities.js';
import { LOBBY_PROP_DEFS, ALL_LOBBY_PROP_IDS, lobbyPropCostForLevel } from '../game/data/lobbyProps.js';
import type { LobbyPropId } from '../game/data/lobbyProps.js';
import { t } from '../i18n/index.js';
import { formatSp } from './formatters.js';

// ── Facility background image mapping ────────────────────

const FACILITY_BG_IMAGES: Record<string, string> = {
  ticket_booth: '/images/views/lobby-bg.webp',
  waiting_lounge: '/images/views/lobby-lounge-bg.webp',
  vip_lounge: '/images/views/lobby-vip-bg.webp',
  poster_wall: '/images/views/lobby-bg.webp',
  snack_cart: '/images/views/lobby-bg.webp',
  souvenir_booth: '/images/views/lobby-bg.webp',
  photo_zone: '/images/views/lobby-bg.webp',
  tv_monitor: '/images/views/lobby-bg.webp',
  costume_rental: '/images/views/lobby-bg.webp',
  fortune_booth: '/images/views/lobby-bg.webp',
};

// ── Action types ─────────────────────────────────────────

export type LobbyViewAction =
  | { action: 'build_floor_facility'; level: number; facilityType: LobbyFacilityType }
  | { action: 'upgrade_floor'; level: number }
  | { action: 'assign_ghost'; floorLevel: number; ghostInstanceId: string }
  | { action: 'unassign_ghost'; floorLevel: number }
  | { action: 'buy_prop'; floorLevel: number; propId: LobbyPropId }
  | { action: 'add_floor' }
  | { action: 'assign_floor_npc'; level: number; ghostInstanceId: string }
  | { action: 'unassign_floor_npc'; level: number }
  | { action: 'demolish_facility'; level: number }
  | { action: 'set_ticket_price'; price: number };

export type LobbyActionCallback = (ev: LobbyViewAction) => void;

// ── Main render function ─────────────────────────────────

export function renderLobbyView(
  container: HTMLElement,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  container.innerHTML = '';

  // Queue header + visitors at top
  renderQueueHeader(container, state);
  renderQueueVisitors(container, state);

  // Building wrapper (column-reverse so floors stack bottom-up)
  const buildingWrap = document.createElement('div');
  buildingWrap.className = 'lobby-building';

  // "Add Floor" button at top (visually)
  renderAddFloorButton(buildingWrap, state, onAction);

  // Render floors top-down (highest level first, column-reverse reverses)
  const floors = [...state.lobbyBuilding].sort((a, b) => b.level - a.level);
  for (const floor of floors) {
    renderLobbyFloor(buildingWrap, floor, state, onAction);
  }

  container.appendChild(buildingWrap);

  // Active teams info
  renderActiveTeamsInfo(container, state);
}

// ── Queue Header ─────────────────────────────────────────

function renderQueueHeader(
  container: HTMLElement,
  state: GameStateData,
): void {
  const queue = state.lobbyWaitQueue || [];
  const section = document.createElement('div');
  section.className = 'lq-header';

  const countEl = document.createElement('span');
  countEl.className = 'lq-header-count';
  countEl.textContent = t('lq_waiting_count', { n: queue.length, max: state.maxQueueSize || 10 });
  section.appendChild(countEl);

  if (queue.length > 0) {
    const avgPatience = Math.round(queue.reduce((s, v) => s + v.patience, 0) / queue.length);
    const avgEl = document.createElement('span');
    avgEl.className = 'lq-header-patience';
    avgEl.textContent = t('lq_avg_patience', { n: avgPatience });
    section.appendChild(avgEl);
  }

  container.appendChild(section);
}

// ── Queue Visitors ───────────────────────────────────────

const VISITOR_COLOR_MAP: Record<string, string> = {
  normal: '#3b82f6',
  brave: '#ef4444',
  couple: '#ec4899',
  scaredy_kid: '#fbbf24',
  coward: '#a3e635',
  youtuber: '#f97316',
  exorcist: '#8b5cf6',
  thrill_seeker: '#06b6d4',
};

function renderQueueVisitors(
  container: HTMLElement,
  state: GameStateData,
): void {
  const queue = state.lobbyWaitQueue || [];
  const section = document.createElement('div');
  section.className = 'lq-visitors-section';

  if (queue.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'lq-visitors-empty';
    empty.textContent = state.dayPhase === 'evening'
      ? t('lq_queue_empty')
      : t('status_closed');
    section.appendChild(empty);
  } else {
    const visitorRow = document.createElement('div');
    visitorRow.className = 'lq-visitor-row';

    for (const visitor of queue) {
      const card = document.createElement('div');
      card.className = 'lq-visitor-card';

      const circle = document.createElement('div');
      circle.className = 'lq-visitor-circle';
      circle.style.background = VISITOR_COLOR_MAP[visitor.type] || '#3b82f6';
      if (visitor.isVip) {
        circle.classList.add('lq-vip');
      }
      card.appendChild(circle);

      const barWrap = document.createElement('div');
      barWrap.className = 'lq-patience-bar-wrap';
      const bar = document.createElement('div');
      bar.className = 'lq-patience-bar';
      const patiencePct = Math.max(0, visitor.patience / visitor.maxPatience * 100);
      bar.style.width = `${patiencePct}%`;
      if (patiencePct <= 30) {
        bar.classList.add('lq-patience-low');
      } else if (patiencePct <= 60) {
        bar.classList.add('lq-patience-mid');
      }
      barWrap.appendChild(bar);
      card.appendChild(barWrap);

      visitorRow.appendChild(card);
    }

    section.appendChild(visitorRow);
  }

  container.appendChild(section);
}

// ── Add Floor Button ─────────────────────────────────────

function renderAddFloorButton(
  container: HTMLElement,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  const addCost = 500 * state.lobbyBuilding.length;
  const canAfford = state.sp >= addCost;

  const zone = document.createElement('div');
  zone.className = 'lb-add-floor-zone';

  const btn = document.createElement('button');
  btn.className = 'lb-add-floor-btn';
  btn.disabled = !canAfford;
  btn.textContent = t('lobby_add_floor', { sp: formatSp(addCost) });

  btn.addEventListener('click', () => {
    onAction({ action: 'add_floor' });
  });
  zone.appendChild(btn);
  container.appendChild(zone);
}

// ── Lobby Floor (same pattern as buildingView.ts renderFloorRoom) ──

function renderLobbyFloor(
  container: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  const floorEl = document.createElement('div');
  floorEl.className = 'lb-floor';
  floorEl.id = `lobby-floor-${floor.level}`;

  // Floor label (left side, vertical)
  const label = document.createElement('div');
  label.className = 'lb-floor-label';
  label.textContent = t('floor_label', { n: floor.level });
  floorEl.appendChild(label);

  // Room area (full width)
  const roomEl = document.createElement('div');
  roomEl.className = 'lb-floor-room';
  floorEl.appendChild(roomEl);

  if (floor.facilityType) {
    renderBuiltFacility(roomEl, floor, state, onAction);
  } else {
    renderEmptyFloor(roomEl, floor, state, onAction);
  }

  container.appendChild(floorEl);
}

// ── Built facility rendering (matches buildingView built room pattern) ──

function renderBuiltFacility(
  roomEl: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  const def = LOBBY_FACILITY_DEFS[floor.facilityType!];
  if (!def) return;

  roomEl.classList.add('built');

  // Background image
  const bgSrc = FACILITY_BG_IMAGES[floor.facilityType!] ?? '/images/views/lobby-bg.webp';
  const bgImg = document.createElement('img');
  bgImg.className = 'lb-room-bg';
  bgImg.src = bgSrc;
  bgImg.alt = '';
  bgImg.draggable = false;
  roomEl.appendChild(bgImg);

  // Facility icon overlay
  const facilityIcon = document.createElement('img');
  facilityIcon.className = 'lb-facility-icon';
  facilityIcon.src = def.imagePath;
  facilityIcon.alt = '';
  facilityIcon.draggable = false;
  roomEl.appendChild(facilityIcon);

  // Ghost NPC staff
  if (floor.assignedGhostId) {
    const ghost = state.ghosts.find((g) => g.id === floor.assignedGhostId);
    if (ghost) {
      const ghostDef = GHOST_DEFS[ghost.characterId];
      if (ghostDef) {
        roomEl.classList.add('has-ghost');

        const ghostImg = document.createElement('img');
        ghostImg.className = 'lb-ghost-img';
        ghostImg.src = ghostDef.image;
        ghostImg.alt = t(ghostDef.nameKey as Parameters<typeof t>[0]);
        ghostImg.draggable = false;
        roomEl.appendChild(ghostImg);

        const ghostName = document.createElement('div');
        ghostName.className = 'lb-ghost-name';
        ghostName.textContent = t(ghostDef.nameKey as Parameters<typeof t>[0]);
        roomEl.appendChild(ghostName);
      }
    }
  }

  // No "staff missing" overlay for any lobby facility — they work without NPC assignment

  // Facility name
  const nameEl = document.createElement('div');
  nameEl.className = 'lb-room-name';
  nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
  roomEl.appendChild(nameEl);

  // Upgrade badge
  if (floor.facilityLevel > 1) {
    const badge = document.createElement('div');
    badge.className = 'lb-upgrade-badge';
    badge.textContent = `+${floor.facilityLevel - 1}`;
    roomEl.appendChild(badge);
  }

  // Props count indicator
  if (floor.props.length > 0) {
    const propsIndicator = document.createElement('div');
    propsIndicator.className = 'lb-props-count';
    propsIndicator.textContent = t('lobby_props_count', { n: floor.props.length });
    roomEl.appendChild(propsIndicator);
  }

  // Click -> 4-tab detail popup
  roomEl.addEventListener('click', () => {
    openFloorDetailPopup(floor, state, onAction);
  });
}

// ── Empty floor rendering (matches buildingView empty room pattern) ──

function renderEmptyFloor(
  roomEl: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  roomEl.classList.add('empty');

  // Empty background
  const bgImg = document.createElement('img');
  bgImg.className = 'lb-room-bg';
  bgImg.src = getEmptyRoomImage(state);
  bgImg.alt = '';
  bgImg.draggable = false;
  roomEl.appendChild(bgImg);

  const buildText = document.createElement('div');
  buildText.className = 'room-name room-name-overlay';
  buildText.textContent = t('lobby_empty_floor');
  roomEl.appendChild(buildText);

  // Click -> facility selection popup
  roomEl.addEventListener('click', () => {
    openFacilitySelectPopup(floor.level, state, onAction);
  });
}

// ── Facility Select Popup (empty floor click) ────────────

function openFacilitySelectPopup(
  floorLevel: number,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  // Floor 1 is reserved for ticket booth — cannot build other facilities
  if (floorLevel === 1) return;

  closeExistingOverlay();

  const overlay = createOverlay();
  const panel = createPanel();

  // Header
  const headerRow = createHeader(t('lobby_select_facility'), () => overlay.remove());
  panel.appendChild(headerRow);

  // Collect built facility types
  const builtTypes = new Set(
    state.lobbyBuilding
      .filter((f) => f.facilityType !== null)
      .map((f) => f.facilityType!),
  );

  const list = document.createElement('div');
  list.className = 'facility-option-list';

  for (const facType of LOBBY_FACILITY_BUILD_ORDER) {
    const def = LOBBY_FACILITY_DEFS[facType];
    if (!def) continue;

    const isBuilt = builtTypes.has(facType);
    const canAfford = state.sp >= def.cost;

    const card = document.createElement('div');
    card.className = 'facility-option-card';
    if (isBuilt) {
      card.classList.add('facility-option-built');
    } else if (!canAfford) {
      card.classList.add('facility-option-disabled');
    }

    // Facility icon image
    const img = document.createElement('img');
    img.className = 'facility-option-img';
    img.src = def.imagePath;
    img.alt = '';
    img.draggable = false;
    card.appendChild(img);

    // Info section
    const info = document.createElement('div');
    info.className = 'facility-option-info';

    const nameRow = document.createElement('div');
    nameRow.className = 'facility-option-name-row';

    const nameEl = document.createElement('div');
    nameEl.className = 'facility-option-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    nameRow.appendChild(nameEl);

    if (isBuilt) {
      const badge = document.createElement('span');
      badge.className = 'facility-option-built-badge';
      badge.textContent = t('lf_built_badge' as Parameters<typeof t>[0]);
      nameRow.appendChild(badge);
    }

    info.appendChild(nameRow);

    // Effect preview
    const effectEl = document.createElement('div');
    effectEl.className = 'facility-option-effect';
    const parts: string[] = [];
    if (def.patienceModifier > 0) {
      parts.push(t('lf_patience_bonus', { n: Math.round(def.patienceModifier * 100) }));
    }
    if (def.revPerVisitor > 0) {
      parts.push(t('lf_revenue_bonus', { n: def.revPerVisitor }));
    }
    if (parts.length > 0) {
      effectEl.textContent = parts.join(' / ');
    } else {
      effectEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
    }
    info.appendChild(effectEl);

    // Cost
    if (!isBuilt) {
      const costEl = document.createElement('div');
      costEl.className = 'facility-option-cost';
      costEl.textContent = formatSp(def.cost) + ' SP';
      info.appendChild(costEl);
    }

    card.appendChild(info);

    if (!isBuilt && canAfford) {
      card.addEventListener('click', () => {
        onAction({ action: 'build_floor_facility', level: floorLevel, facilityType: facType });
        overlay.remove();
      });
    }

    list.appendChild(card);
  }

  panel.appendChild(list);
  overlay.appendChild(panel);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
    }
  });

  getPopupContainer().appendChild(overlay);
}

// ── 4-Tab Floor Detail Popup (built floor click) ─────────

type DetailTab = 'info' | 'objects' | 'upgrade' | 'demolish';

function openFloorDetailPopup(
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
): void {
  if (!floor.facilityType) return;

  const def = LOBBY_FACILITY_DEFS[floor.facilityType];
  if (!def) return;

  closeExistingOverlay();

  const overlay = createOverlay();
  const panel = createPanel();

  // Facility image header
  const imgHeader = document.createElement('div');
  imgHeader.className = 'facility-detail-header';

  const facilityImg = document.createElement('img');
  facilityImg.className = 'facility-detail-img';
  facilityImg.src = def.imagePath;
  facilityImg.alt = '';
  facilityImg.draggable = false;
  imgHeader.appendChild(facilityImg);

  const facilityNameEl = document.createElement('div');
  facilityNameEl.className = 'facility-detail-name';
  facilityNameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
  imgHeader.appendChild(facilityNameEl);

  const facilityLevelEl = document.createElement('div');
  facilityLevelEl.className = 'facility-detail-level';
  facilityLevelEl.textContent = t('facility_level', { n: floor.facilityLevel });
  imgHeader.appendChild(facilityLevelEl);

  panel.appendChild(imgHeader);

  // Header with close button
  const headerRow = document.createElement('div');
  headerRow.className = 'lb-detail-header';

  const nameWrap = document.createElement('div');
  nameWrap.appendChild(document.createTextNode(''));

  headerRow.appendChild(nameWrap);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'lb-detail-close';
  closeBtn.textContent = 'X';
  closeBtn.addEventListener('click', () => overlay.remove());
  headerRow.appendChild(closeBtn);

  panel.appendChild(headerRow);

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.className = 'lb-tab-bar';

  // Floor 1 (ticket booth) is permanent — no demolish tab
  const isTicketBoothFloor = floor.level === 1;

  const tabs: { id: DetailTab; label: string }[] = [
    { id: 'info', label: t('lobby_tab_info') },
    { id: 'objects', label: t('lobby_tab_objects') },
    { id: 'upgrade', label: t('lobby_tab_upgrade') },
    ...(isTicketBoothFloor ? [] : [{ id: 'demolish' as DetailTab, label: t('lobby_tab_demolish') }]),
  ];

  const tabContent = document.createElement('div');
  tabContent.className = 'lb-tab-content';

  function renderTabContent(tab: DetailTab): void {
    tabContent.innerHTML = '';

    // Update tab button states
    tabBar.querySelectorAll<HTMLButtonElement>('.lb-tab-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    });

    switch (tab) {
      case 'info':
        renderInfoTab(tabContent, floor, state, onAction, overlay);
        break;
      case 'objects':
        renderObjectsTab(tabContent, floor, state, onAction, overlay);
        break;
      case 'upgrade':
        renderUpgradeTab(tabContent, floor, state, onAction, overlay);
        break;
      case 'demolish':
        renderDemolishTab(tabContent, floor, state, onAction, overlay);
        break;
    }
  }

  for (const tab of tabs) {
    const tabBtn = document.createElement('button');
    tabBtn.className = 'lb-tab-btn';
    tabBtn.dataset.tab = tab.id;
    tabBtn.textContent = tab.label;
    if (tab.id === 'info') tabBtn.classList.add('active');
    tabBtn.addEventListener('click', () => renderTabContent(tab.id));
    tabBar.appendChild(tabBtn);
  }

  panel.appendChild(tabBar);
  panel.appendChild(tabContent);

  // Initial tab
  renderTabContent('info');

  overlay.appendChild(panel);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
    }
  });

  getPopupContainer().appendChild(overlay);
}

// ── Ticket Price Section (inside ticket booth Info tab) ───

function renderTicketPriceSection(
  container: HTMLElement,
  state: GameStateData,
  onAction: LobbyActionCallback,
  overlay: HTMLElement,
): void {
  const section = document.createElement('div');
  section.className = 'lb-ticket-price-section';

  const label = document.createElement('div');
  label.className = 'lb-ticket-price-label';
  label.textContent = t('ticket_price_setting' as Parameters<typeof t>[0]);
  section.appendChild(label);

  const priceRow = document.createElement('div');
  priceRow.className = 'lb-ticket-price-row';

  const minusBtn = document.createElement('button');
  minusBtn.className = 'lb-ticket-btn';
  minusBtn.textContent = '-';
  minusBtn.disabled = state.ticketPrice <= 5;
  minusBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const newPrice = Math.max(5, state.ticketPrice - 5);
    onAction({ action: 'set_ticket_price', price: newPrice });
    overlay.remove();
  });
  priceRow.appendChild(minusBtn);

  const priceDisplay = document.createElement('div');
  priceDisplay.className = 'lb-ticket-price-display';
  priceDisplay.textContent = `${state.ticketPrice} SP`;
  priceRow.appendChild(priceDisplay);

  const plusBtn = document.createElement('button');
  plusBtn.className = 'lb-ticket-btn';
  plusBtn.textContent = '+';
  plusBtn.disabled = state.ticketPrice >= 200;
  plusBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const newPrice = Math.min(200, state.ticketPrice + 5);
    onAction({ action: 'set_ticket_price', price: newPrice });
    overlay.remove();
  });
  priceRow.appendChild(plusBtn);

  section.appendChild(priceRow);

  // Price hint
  const hint = document.createElement('div');
  hint.className = 'lb-ticket-price-hint';
  hint.textContent = state.ticketPrice <= 10
    ? t('ticket_price_low')
    : t('ticket_price_high');
  section.appendChild(hint);

  container.appendChild(section);
}

// ── Tab: Info ────────────────────────────────────────────

function renderInfoTab(
  container: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
  overlay: HTMLElement,
): void {
  const def = LOBBY_FACILITY_DEFS[floor.facilityType!]!;

  // Facility image (icon)
  const imgEl = document.createElement('img');
  imgEl.className = 'lb-detail-facility-img';
  imgEl.src = def.imagePath;
  imgEl.alt = '';
  imgEl.draggable = false;
  container.appendChild(imgEl);

  // Description
  const descEl = document.createElement('div');
  descEl.className = 'lb-detail-desc';
  descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
  container.appendChild(descEl);

  // Ticket booth: ticket price setting UI
  if (floor.facilityType === 'ticket_booth') {
    renderTicketPriceSection(container, state, onAction, overlay);
  }

  // Stats
  const statsEl = document.createElement('div');
  statsEl.className = 'lf-detail-stats';

  const totalPatience = def.patienceModifier + def.patiencePerLevel * (floor.facilityLevel - 1);
  if (totalPatience > 0) {
    const row = document.createElement('div');
    row.className = 'lf-stat-row';
    row.textContent = t('lf_patience_bonus', { n: Math.round(totalPatience * 100) });
    statsEl.appendChild(row);
  }

  const totalRev = def.revPerVisitor + def.revenuePerLevel * (floor.facilityLevel - 1);
  if (totalRev > 0) {
    const row = document.createElement('div');
    row.className = 'lf-stat-row';
    row.textContent = t('lf_revenue_bonus', { n: totalRev });
    statsEl.appendChild(row);
  }

  if (floor.assignedGhostId) {
    const row = document.createElement('div');
    row.className = 'lf-stat-row lf-stat-npc';
    row.textContent = t('lf_npc_bonus');
    statsEl.appendChild(row);
  }

  // Unique effect display
  if (def.uniqueEffect) {
    const ueRow = document.createElement('div');
    ueRow.className = 'lf-stat-row lf-stat-unique';
    ueRow.textContent = t(def.uniqueEffect.descKey as Parameters<typeof t>[0]);
    statsEl.appendChild(ueRow);
  }

  container.appendChild(statsEl);

  // NPC Staff section
  const staffDiv = document.createElement('div');
  staffDiv.className = 'lb-detail-staff';

  const staffLabel = document.createElement('div');
  staffLabel.className = 'lb-detail-section-title';
  staffLabel.textContent = t('facility_staff');
  staffDiv.appendChild(staffLabel);

  if (floor.assignedGhostId) {
    const ghost = state.ghosts.find((g) => g.id === floor.assignedGhostId);
    if (ghost) {
      const ghostDef = GHOST_DEFS[ghost.characterId];
      if (ghostDef) {
        const staffInfo = document.createElement('div');
        staffInfo.className = 'lb-staff-info';

        const avatar = document.createElement('img');
        avatar.className = 'lb-staff-avatar';
        avatar.src = ghostDef.image;
        avatar.alt = t(ghostDef.nameKey as Parameters<typeof t>[0]);
        avatar.draggable = false;
        staffInfo.appendChild(avatar);

        const staffName = document.createElement('span');
        staffName.textContent = `${t(ghostDef.nameKey as Parameters<typeof t>[0])} Lv.${ghost.level}`;
        staffInfo.appendChild(staffName);

        staffDiv.appendChild(staffInfo);

        const unassignBtn = document.createElement('button');
        unassignBtn.className = 'lb-action-btn secondary';
        unassignBtn.textContent = t('facility_unassign_staff');
        unassignBtn.addEventListener('click', () => {
          onAction({ action: 'unassign_ghost', floorLevel: floor.level });
          overlay.remove();
        });
        staffDiv.appendChild(unassignBtn);
      }
    }
  } else {
    const noStaff = document.createElement('div');
    noStaff.className = 'lb-no-staff';
    noStaff.textContent = t('facility_staff_none');
    staffDiv.appendChild(noStaff);

    const available = getAvailableGhosts(state);
    if (available.length > 0) {
      const select = document.createElement('select');
      select.className = 'lb-staff-select';
      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = t('facility_assign_staff');
      select.appendChild(defaultOpt);

      for (const ghost of available) {
        const ghostDef = GHOST_DEFS[ghost.characterId];
        if (!ghostDef) continue;
        const opt = document.createElement('option');
        opt.value = ghost.id;
        opt.textContent = `${t(ghostDef.nameKey as Parameters<typeof t>[0])} Lv.${ghost.level}`;
        select.appendChild(opt);
      }

      select.addEventListener('change', () => {
        if (select.value) {
          onAction({ action: 'assign_ghost', floorLevel: floor.level, ghostInstanceId: select.value });
          overlay.remove();
        }
      });
      staffDiv.appendChild(select);
    }
  }

  container.appendChild(staffDiv);
}

// ── Tab: Objects ─────────────────────────────────────────

function renderObjectsTab(
  container: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
  overlay: HTMLElement,
): void {
  const propsDiv = document.createElement('div');
  propsDiv.className = 'lb-detail-props';

  // Show only props available for this facility type
  const facilityDef = floor.facilityType ? LOBBY_FACILITY_DEFS[floor.facilityType] : null;
  const availableProps: LobbyPropId[] = facilityDef?.availableProps ?? ALL_LOBBY_PROP_IDS;

  for (const propId of availableProps) {
    const propDef = LOBBY_PROP_DEFS[propId];
    if (!propDef) continue;
    const existing = floor.props.find((p) => p.propId === propId);
    const currentLevel = existing ? existing.level : 0;
    const isMaxed = currentLevel >= propDef.maxLevel;

    const propRow = document.createElement('div');
    propRow.className = 'lb-prop-row';

    const propInfo = document.createElement('div');
    propInfo.className = 'lb-prop-info';

    const propName = document.createElement('div');
    propName.className = 'lb-prop-name';
    propName.textContent = t(propDef.nameKey as Parameters<typeof t>[0]);
    if (currentLevel > 0) {
      propName.textContent += ` ${t('prop_level', { n: currentLevel })}`;
    }
    propInfo.appendChild(propName);

    const propDesc = document.createElement('div');
    propDesc.className = 'lb-prop-desc';
    propDesc.textContent = t(propDef.descKey as Parameters<typeof t>[0]);
    propInfo.appendChild(propDesc);

    propRow.appendChild(propInfo);

    if (isMaxed) {
      const maxed = document.createElement('div');
      maxed.className = 'lb-prop-maxed';
      maxed.textContent = t('prop_max');
      propRow.appendChild(maxed);
    } else {
      const nextLevel = currentLevel + 1;
      const cost = lobbyPropCostForLevel(propId, nextLevel);
      const btn = document.createElement('button');
      btn.className = 'lb-prop-btn';
      btn.disabled = state.sp < cost;
      btn.textContent = currentLevel === 0
        ? t('prop_buy', { sp: formatSp(cost) })
        : t('prop_upgrade', { sp: formatSp(cost) });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        onAction({ action: 'buy_prop', floorLevel: floor.level, propId: propId as LobbyPropId });
        overlay.remove();
      });
      propRow.appendChild(btn);
    }

    propsDiv.appendChild(propRow);
  }

  container.appendChild(propsDiv);
}

// ── Tab: Upgrade ─────────────────────────────────────────

function renderUpgradeTab(
  container: HTMLElement,
  floor: LobbyFloor,
  state: GameStateData,
  onAction: LobbyActionCallback,
  overlay: HTMLElement,
): void {
  const def = LOBBY_FACILITY_DEFS[floor.facilityType!]!;
  const section = document.createElement('div');
  section.className = 'lb-upgrade-section';

  if (floor.facilityLevel >= def.maxLevel) {
    const maxedEl = document.createElement('div');
    maxedEl.className = 'lb-maxed';
    maxedEl.textContent = t('lobby_upgrade_max');
    section.appendChild(maxedEl);
  } else {
    const nextLevel = floor.facilityLevel + 1;
    const cost = lobbyFacilityUpgradeCost(floor.facilityType!, nextLevel);

    // Current -> Next level info
    const levelInfo = document.createElement('div');
    levelInfo.className = 'lb-upgrade-level-info';
    levelInfo.textContent = t('lobby_upgrade_next', { from: floor.facilityLevel, to: nextLevel });
    section.appendChild(levelInfo);

    // Effect after upgrade
    const effectLabel = document.createElement('div');
    effectLabel.className = 'lb-upgrade-effect-label';
    effectLabel.textContent = t('lobby_upgrade_effect');
    section.appendChild(effectLabel);

    const effectEl = document.createElement('div');
    effectEl.className = 'lb-upgrade-effect';

    const parts: string[] = [];
    const nextPatience = def.patienceModifier + def.patiencePerLevel * (nextLevel - 1);
    if (nextPatience > 0) {
      parts.push(t('lf_patience_bonus', { n: Math.round(nextPatience * 100) }));
    }
    const nextRev = def.revPerVisitor + def.revenuePerLevel * (nextLevel - 1);
    if (nextRev > 0) {
      parts.push(t('lf_revenue_bonus', { n: nextRev }));
    }
    if (parts.length > 0) {
      effectEl.textContent = parts.join(' / ');
    }
    section.appendChild(effectEl);

    // Upgrade button
    const upgradeBtn = document.createElement('button');
    upgradeBtn.className = 'lb-action-btn primary';
    upgradeBtn.disabled = state.sp < cost;
    upgradeBtn.textContent = t('facility_detail_upgrade', { sp: formatSp(cost) });
    upgradeBtn.addEventListener('click', () => {
      onAction({ action: 'upgrade_floor', level: floor.level });
      overlay.remove();
    });
    section.appendChild(upgradeBtn);
  }

  container.appendChild(section);
}

// ── Tab: Demolish ────────────────────────────────────────

function renderDemolishTab(
  container: HTMLElement,
  floor: LobbyFloor,
  _state: GameStateData,
  onAction: LobbyActionCallback,
  overlay: HTMLElement,
): void {
  const section = document.createElement('div');
  section.className = 'lb-demolish-section';

  const warningText = document.createElement('div');
  warningText.className = 'lb-demolish-warning';
  warningText.textContent = t('lobby_demolish_confirm');
  section.appendChild(warningText);

  const demolishBtn = document.createElement('button');
  demolishBtn.className = 'lb-action-btn lb-demolish-btn';
  demolishBtn.textContent = t('lobby_demolish_btn');
  demolishBtn.addEventListener('click', () => {
    onAction({ action: 'demolish_facility', level: floor.level });
    overlay.remove();
  });
  section.appendChild(demolishBtn);

  container.appendChild(section);
}

// ── Active Teams Info ────────────────────────────────────

function renderActiveTeamsInfo(
  container: HTMLElement,
  state: GameStateData,
): void {
  if (state.visitorGroup && !state.visitorGroup.done) {
    const teamsInfo = document.createElement('div');
    teamsInfo.className = 'lb-teams-info';
    teamsInfo.textContent = t('lobby_active_teams', {
      cur: 1,
      max: state.maxConcurrentTeams,
    });
    container.appendChild(teamsInfo);
  }
}

// ── Shared overlay/panel helpers ─────────────────────────

function closeExistingOverlay(): void {
  // Close both old and new style overlays
  const prev = document.querySelector('.lb-detail-overlay');
  if (prev) prev.remove();
  const prevPopup = document.querySelector('.lobby-popup-overlay');
  if (prevPopup) prevPopup.remove();
}

function getPopupContainer(): HTMLElement {
  return document.getElementById('popup-container') || document.body;
}

function createOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay lobby-popup-overlay';
  return overlay;
}

function createPanel(): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'popup-box lobby-popup-box';
  return panel;
}

function createHeader(title: string, onClose: () => void): HTMLElement {
  const headerRow = document.createElement('div');
  headerRow.className = 'lb-detail-header';

  const titleEl = document.createElement('div');
  titleEl.className = 'lb-detail-name';
  titleEl.textContent = title;
  headerRow.appendChild(titleEl);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'lb-detail-close';
  closeBtn.textContent = 'X';
  closeBtn.addEventListener('click', onClose);
  headerRow.appendChild(closeBtn);

  return headerRow;
}

// ── Helpers ──────────────────────────────────────────────

function getAvailableGhosts(state: GameStateData) {
  return state.ghosts.filter((g) => {
    if (g.assignedRoomId || g.training || g.academySession || g.assignedFacilityId) return false;
    if (state.lobbyStaff.includes(g.id)) return false;
    if (state.lobbyBuilding.some((f) => f.assignedGhostId === g.id)) return false;
    if (state.facilityBuilding.some((f) => f.assignedGhostId === g.id)) return false;
    return true;
  });
}
